import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    Archive,
    CalendarClock,
    Loader2,
    RefreshCw,
    ScanText,
    ShieldCheck,
    XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function Stat({ label, value, tone = "slate" }) {
    const tones = {
        violet: "border-violet-500/30 bg-violet-500/10 text-violet-200",
        emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
        amber: "border-amber-500/30 bg-amber-500/10 text-amber-200",
        red: "border-red-500/30 bg-red-500/10 text-red-200",
        slate: "border-white/10 bg-white/5 text-slate-300",
    };
    return (
        <div className={`rounded-lg border px-3 py-2 ${tones[tone] || tones.slate}`}>
            <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
            <div className="text-xl font-bold mt-0.5">{value}</div>
        </div>
    );
}

export default function VekaletMerkezPanel({ arsivMode = false, onArsivModeChange }) {
    const [ozet, setOzet] = useState(null);
    const [uyarilar, setUyarilar] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ocrBusy, setOcrBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [o, u] = await Promise.all([
                api.get("/v1/admin/vekaletler/merkez-ozet/"),
                api.get(
                    `/v1/admin/vekaletler/uyarilar/?limit=12&arsiv=${arsivMode ? "1" : "0"}`,
                ),
            ]);
            setOzet(o.data);
            setUyarilar(u.data?.uyarilar || []);
        } catch {
            setOzet(null);
            setUyarilar([]);
        } finally {
            setLoading(false);
        }
    }, [arsivMode]);

    useEffect(() => {
        load();
    }, [load]);

    const topluOcr = async () => {
        setOcrBusy(true);
        try {
            const r = await api.post("/v1/admin/vekaletler/toplu-ocr/", { limit: 150 });
            toast.success(
                `OCR tamamlandı: ${r.data?.basarili || 0} başarılı, ${r.data?.hatali || 0} hatalı`,
            );
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Toplu OCR başarısız");
        } finally {
            setOcrBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="mb-6 flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Vekalet merkezi yükleniyor…
            </div>
        );
    }

    if (!ozet) return null;

    return (
        <div className="mb-6 rounded-2xl border border-violet-500/25 bg-gradient-to-br from-violet-500/10 via-[#0B0F19]/95 to-[#121A2F]/90 overflow-hidden shadow-lg">
            <div className="px-5 py-4 border-b border-white/10 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                        <ShieldCheck className="h-5 w-5 text-violet-300" />
                    </div>
                    <div>
                        <div className="text-base font-bold text-slate-100">Vekalet Merkezi</div>
                        <div className="text-xs text-slate-500">
                            Aktif vekaletler · arşiv · günlük tek özet mail
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {!arsivMode && (
                        <button
                            type="button"
                            disabled={ocrBusy}
                            onClick={topluOcr}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold disabled:opacity-50"
                        >
                            {ocrBusy ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <ScanText className="h-3.5 w-3.5" />
                            )}
                            Bekleyen OCR tara ({ozet.ocr_bekleyen + ozet.ocr_hatali})
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={load}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/10 text-slate-300 text-xs hover:bg-white/5"
                    >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Yenile
                    </button>
                </div>
            </div>

            <div className="px-4 pt-3 flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() => onArsivModeChange?.(false)}
                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold border ${
                        !arsivMode
                            ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-100"
                            : "border-white/10 text-slate-400 hover:bg-white/5"
                    }`}
                >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Aktif vekaletler ({ozet.toplam_aktif ?? ozet.toplam})
                </button>
                <button
                    type="button"
                    onClick={() => onArsivModeChange?.(true)}
                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold border ${
                        arsivMode
                            ? "border-amber-500/40 bg-amber-500/15 text-amber-100"
                            : "border-white/10 text-slate-400 hover:bg-white/5"
                    }`}
                >
                    <Archive className="h-3.5 w-3.5" />
                    Arşiv ({ozet.arsiv_toplam ?? 0})
                </button>
            </div>

            <div className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
                {!arsivMode ? (
                    <>
                        <Stat label="Aktif listede" value={ozet.toplam_aktif ?? ozet.toplam} />
                        <Stat label="Geçerli" value={ozet.aktif} tone="emerald" />
                        <Stat label="7 gün içinde" value={ozet.yaklasan_7_gun} tone="amber" />
                        <Stat label="Süresiz" value={ozet.tarihsiz} tone="slate" />
                        <Stat label="Arşivde dolmuş" value={ozet.arsiv_dolmus ?? ozet.suresi_dolmus} tone="red" />
                        <Stat label="OCR tamam" value={ozet.ocr_tamamlanan} tone="emerald" />
                        <Stat label="OCR bekleyen" value={ozet.ocr_bekleyen} tone="violet" />
                        <Stat label="OCR hatalı" value={ozet.ocr_hatali} tone="red" />
                    </>
                ) : (
                    <>
                        <Stat label="Arşiv toplam" value={ozet.arsiv_toplam ?? 0} tone="amber" />
                        <Stat label="Süresi dolmuş" value={ozet.arsiv_dolmus ?? 0} tone="red" />
                        <Stat label="Aktif listede" value={ozet.toplam_aktif ?? ozet.toplam} tone="emerald" />
                        <Stat label="7 gün içinde" value={ozet.yaklasan_7_gun} tone="amber" />
                    </>
                )}
            </div>

            {!arsivMode && uyarilar.length > 0 && (
                <div className="px-4 pb-4">
                    <div className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold mb-2 flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Yaklaşan vekaletler (aktif)
                    </div>
                    <div className="space-y-2 max-h-52 overflow-auto">
                        {uyarilar.map((u) => (
                            <div
                                key={u.vekalet_id}
                                className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${
                                    u.seviye === "doldu"
                                        ? "border-red-500/30 bg-red-500/10 text-red-200"
                                        : u.seviye === "kritik"
                                          ? "border-amber-500/35 bg-amber-500/10 text-amber-200"
                                          : "border-yellow-500/25 bg-yellow-500/5 text-yellow-100"
                                }`}
                            >
                                <div>
                                    <span className="font-mono-jb text-[10px] opacity-80">
                                        #{u.vekalet_id}
                                    </span>
                                    <span className="font-semibold ml-2">{u.muvekkil_ad || "—"}</span>
                                    <span className="text-[10px] opacity-80 ml-2">
                                        Bitiş: {u.bitis_tarihi || "Süresiz"}
                                        {u.kalan_gun != null ? ` · ${u.kalan_gun} gün` : ""}
                                        {u.dosya_sayisi != null ? ` · ${u.dosya_sayisi} dosya` : ""}
                                    </span>
                                </div>
                                <Link
                                    to={`/e/vekaletler/${u.vekalet_id}`}
                                    className="text-[10px] font-semibold underline"
                                >
                                    Aç
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {arsivMode && (
                <div className="mx-4 mb-4 px-3 py-2 rounded-lg border border-amber-500/25 bg-amber-500/10 text-[11px] text-amber-100 flex items-center gap-2">
                    <Archive className="h-4 w-4 shrink-0" />
                    Arşivdeki vekaletler erişilebilir durumdadır. Süresiz vekaletler arşive
                    alınmaz; yalnızca bitiş tarihi geçmiş süreli kayıtlar burada listelenir.
                </div>
            )}

            {!arsivMode && ozet.tarihsiz > 0 && (
                <div className="mx-4 mb-4 px-3 py-2 rounded-lg border border-violet-500/25 bg-violet-500/10 text-[11px] text-violet-200 flex items-center gap-2">
                    <CalendarClock className="h-4 w-4 shrink-0" />
                    {ozet.tarihsiz} vekalette bitiş tarihi yok (çoğu süresiz) — gerekirse OCR ile
                    okutabilirsiniz.
                </div>
            )}
        </div>
    );
}

export function VekaletUyariBanner({ uyari }) {
    if (!uyari || uyari.seviye === "aktif" || uyari.seviye === "bilinmiyor") return null;
    const kritik = uyari.seviye === "kritik" || uyari.seviye === "doldu";
    return (
        <div
            className={`mb-4 rounded-xl border px-4 py-3 flex items-start gap-3 ${
                uyari.seviye === "doldu"
                    ? "border-red-500/35 bg-red-500/10"
                    : kritik
                      ? "border-amber-500/35 bg-amber-500/10"
                      : "border-yellow-500/25 bg-yellow-500/5"
            }`}
        >
            {uyari.seviye === "doldu" ? (
                <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
            ) : (
                <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            )}
            <div>
                <div className="text-sm font-semibold text-slate-100">Vekalet uyarısı</div>
                <p className="text-xs text-slate-400 mt-0.5">{uyari.mesaj}</p>
            </div>
        </div>
    );
}

export function OcrDurumBadge({ durum, hata }) {
    const map = {
        TAMAMLANDI: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
        ISLENIYOR: "text-sky-400 border-sky-500/30 bg-sky-500/10",
        BEKLIYOR: "text-amber-300 border-amber-500/30 bg-amber-500/10",
        HATA: "text-red-400 border-red-500/30 bg-red-500/10",
    };
    const label = {
        TAMAMLANDI: "OCR tamam",
        ISLENIYOR: "OCR işleniyor",
        BEKLIYOR: "OCR bekliyor",
        HATA: "OCR hatası",
    };
    const cls = map[durum] || map.BEKLIYOR;
    return (
        <span
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${cls}`}
            title={hata || ""}
        >
            <ScanText className="h-3 w-3" />
            {label[durum] || durum || "OCR bekliyor"}
        </span>
    );
}
